import { useEffect, useState } from "react";

import type { FAQDocumentWithTask } from "@frontierx/shared/schemas/FAQ";
import * as Sentry from "@sentry/react";
import { HelpCircle, Pencil, Plus, Trash2 } from "lucide-react";

import { useToast } from "@/components/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

export interface FAQListProps {
  faqs: FAQDocumentWithTask[];
  isLoading: boolean;
  isError: boolean;
  onDelete: (id: string) => void;
  onAdd: (faq: { question: string; idealAnswer: string }) => Promise<boolean>;
  onEdit: (
    id: string,
    faq: { question: string; idealAnswer: string },
  ) => Promise<boolean>;
  isDeleting: boolean;
}

interface FAQDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (faq: {
    question: string;
    idealAnswer: string;
  }) => Promise<boolean>;
  initialData?: { question: string; idealAnswer: string };
  mode: "add" | "edit";
}

const FAQDialog = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode,
}: FAQDialogProps) => {
  const [question, setQuestion] = useState(initialData?.question ?? "");
  const [idealAnswer, setIdealAnswer] = useState(
    initialData?.idealAnswer ?? "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (initialData) {
      setQuestion(initialData.question);
      setIdealAnswer(initialData.idealAnswer);
    } else {
      setQuestion("");
      setIdealAnswer("");
    }
  }, [initialData]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const success = await onSubmit({ question, idealAnswer });
      if (success) {
        onClose();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${mode} FAQ. Please try again.`,
        variant: "destructive",
      });
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add FAQ" : "Edit FAQ"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-4">
            <Label className="text-sm font-medium">Question</Label>
            <Input
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What is the expected delivery time?"
            />
          </div>
          <div className="space-y-4">
            <Label className="text-sm font-medium">Ideal Answer</Label>
            <Textarea
              value={idealAnswer}
              onChange={(e) => setIdealAnswer(e.target.value)}
              placeholder="Our standard delivery time is 3-5 business days..."
              rows={4}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!question || !idealAnswer || isSubmitting}
          >
            {isSubmitting
              ? `${mode === "add" ? "Adding" : "Saving"}...`
              : mode === "add"
                ? "Add FAQ"
                : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function FAQList(props: Readonly<FAQListProps>) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQDocumentWithTask | null>(
    null,
  );

  const handleEdit = (faq: FAQDocumentWithTask) => {
    setEditingFaq(faq);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingFaq(null);
  };

  const handleSubmit = async (data: {
    question: string;
    idealAnswer: string;
  }) => {
    if (editingFaq) {
      return await props.onEdit(editingFaq.id, data);
    }
    return await props.onAdd(data);
  };

  return (
    <div className="flex h-full flex-col space-y-8 p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Frequently Asked Questions</h2>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add FAQ
        </Button>
      </div>
      <Card className="flex-grow overflow-hidden">
        <ScrollArea className="h-full">
          <Table>
            <TableBody>
              {props.faqs.map((faq) => (
                <TableRow key={faq.id}>
                  <TableCell className="w-5/6">
                    <HelpCircle className="-mt-1 mr-2 inline-block h-4 w-4" />
                    {faq.question}
                  </TableCell>
                  <TableCell className="w-1/6 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => handleEdit(faq)}
                        disabled={props.isDeleting}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => props.onDelete(faq.id)}
                        disabled={props.isDeleting}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
      <FAQDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        initialData={
          editingFaq
            ? {
                question: editingFaq.question,
                idealAnswer: editingFaq.idealAnswer,
              }
            : undefined
        }
        mode={editingFaq ? "edit" : "add"}
      />
    </div>
  );
}
